import type { SelectOption } from './select-field';

/**
 * The font-family choices offered by the text inspectors (`textbox-inspector`,
 * `placeholder-text-inspector`). Single source of truth so the two inspectors
 * cannot drift.
 *
 * TWO hard requirements for every family listed here:
 *
 * 1. It MUST support Cyrillic. The product is Russian-language, so a font that
 *    renders Latin but falls back for Cyrillic is worse than useless — verified
 *    via the font cmap (local woff2) or the Google Fonts `cyrillic` subset.
 *    This is why Helvetica (no Cyrillic + not installed on Windows → falls back
 *    to Arial), Big Shoulders Display and Saira Stencil One (Latin-only Google
 *    fonts) are intentionally absent.
 *
 * 2. It MUST actually be loaded, otherwise both the Fabric canvas and the
 *    headless render pipeline silently fall back and the picked font is lost.
 *    Loading sources for the families below:
 *      - System / web-safe (ship with Windows incl. Cyrillic): Arial, Verdana,
 *        Georgia, Times New Roman, Courier New.
 *      - Local `@font-face` (src/index.css): SF Pro Display, SF Pro Text.
 *      - Google via the WebFont loader (public/index.html `WebFont.load`):
 *        Open Sans, Montserrat, Oswald.
 *
 * Adding a family here requires satisfying BOTH: confirm Cyrillic coverage and
 * wire its loading (a new `@font-face` or a new entry in the WebFont
 * `families` list — with the weights the «Начертание» picker offers).
 *
 * The `value` is the bare family name stored in `data.fontFamily` / set on the
 * Fabric object; `style.fontFamily` renders each option (and the closed
 * control) in the font it names, so the picker doubles as a live preview.
 */
const family = (name: string): SelectOption => ({
  value: name,
  label: name,
  style: { fontFamily: `"${name}", sans-serif` },
});

export const FONT_FAMILY_OPTIONS: SelectOption[] = [
  family('SF Pro Display'),
  family('SF Pro Text'),
  family('Open Sans'),
  family('Montserrat'),
  family('Oswald'),
  family('Arial'),
  family('Verdana'),
  family('Georgia'),
  family('Times New Roman'),
  family('Courier New'),
];
